import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EyeOff, Pencil, Trash2 } from 'lucide-react';
import { Project } from '../types';
import { localized, mediaUrl, yearOf, titleOf } from '../utils';
import { BACKEND_URL } from '../config';
import ProjectLinks from './ProjectLinks';

/** `onDelete` set = admin mode: edit and delete buttons beside the row */
export default function ProjectRow({ project: p, onDelete }: { project: Project; onDelete?: (p: Project) => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const logo = mediaUrl(p.logo, `${BACKEND_URL}/projects/${p.strid}`);
  const title = titleOf(p, lang);

  return (
    <div className="group relative flex items-center gap-1 border-b border-line">
    <div className="flex flex-1 min-w-0 flex-wrap items-center gap-x-5 gap-y-2 py-5">
      <span className="w-11 h-11 flex-none rounded-xl overflow-hidden bg-surface-2 flex items-center justify-center font-display font-bold text-primary-light">
        {logo ? <img src={logo} alt="" className="w-full h-full object-cover" /> : title[0]}
      </span>
      <span className="flex-[1_1_220px] font-display font-bold tracking-[-0.03em] text-[28px] sm:text-[34px] leading-tight text-ink group-hover:text-primary-light transition-colors">
        {/* stretched link: whole row opens the project, buttons sit above it */}
        <Link to={`/projects/${p.strid}`} className="after:absolute after:inset-0">{title}</Link>
        {!p.is_public && <EyeOff size={16} className="inline ml-2 text-zinc-500" aria-label="Private" />}
        <span className="inline-block align-middle ml-3"><ProjectLinks project={p} compact className="text-zinc-400 hover:text-ink hover:bg-surface" /></span>
      </span>
      <span className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-muted">
        <span>{localized(p.type, lang)}</span>
        <span className="font-semibold text-ink">{p.not_started ? '—' : yearOf(p.start_date)}</span>
      </span>
    </div>
    {onDelete && (
      <>
        <Link to={`/projects/edit/${p.strid}`} aria-label={`${t('edit')} ${title}`} className="relative z-10 w-11 h-11 flex-none rounded-full flex items-center justify-center text-zinc-400 hover:text-ink hover:bg-surface"><Pencil size={16} /></Link>
        <button type="button" onClick={() => onDelete(p)} aria-label={`${t('delete')} ${title}`} className="relative z-10 w-11 h-11 flex-none rounded-full flex items-center justify-center text-zinc-400 hover:text-red-500 hover:bg-surface"><Trash2 size={16} /></button>
      </>
    )}
    </div>
  );
}
